import { useState } from "react";
import { Tabs } from "expo-router";
import { View, Text, Image, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useChannels } from "@/hooks/useChannels";
import { colors } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";
import { HomeIcon, CalendarIcon, ChatIcon, TicketIcon, ShieldIcon } from "@/components/TabIcons";
import NotificationBell from "@/components/NotificationBell";
import NotificationsDrawer from "@/components/NotificationsDrawer";

// shortName is a display abbreviation (e.g. "PKCC"), never the internal slug
function getDisplayName(club: any): string {
  if (!club) return "inthecrease";
  // If shortName looks like a real abbreviation (short, no hyphens), use it
  if (club.shortName && club.shortName.length <= 10 && !/[-_]/.test(club.shortName)) {
    return club.shortName;
  }
  return club.name || "inthecrease";
}

function HeaderTitle() {
  const { club } = useClub();
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      {club?.logoUrl ? (
        <Image source={{ uri: club.logoUrl }} style={{ width: 26, height: 26, borderRadius: 6 }} />
      ) : (
        <StumpsIcon size={22} />
      )}
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        style={{ fontSize: 15, fontWeight: "800", color: colors.ink, letterSpacing: -0.2, flexShrink: 1 }}
      >
        {getDisplayName(club)}
      </Text>
    </View>
  );
}

function useNeedsPaymentSetup() {
  const { membership, club } = useClub();
  if (!club || !membership) return false;
  if (membership.role !== "admin") return false;
  if (club.paymentsDeclined) return false;
  if (club.stripeOnboardingComplete) return false;
  return true;
}

export default function TabsLayout() {
  const insets = useSafeAreaInsets();
  const nativeBottom = Math.max(insets.bottom, 12);
  const bottomPadding = Platform.OS === "web" ? 10 : nativeBottom;
  const tabHeight = Platform.OS === "web" ? 56 : 50 + nativeBottom;
  const needsPaymentSetup = useNeedsPaymentSetup();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();
  const { club } = useClub();
  const { unreadChannelCount } = useChannels(club?.id, user?.uid);

  return (
    <View style={{ flex: 1 }}>
    <Tabs
      screenOptions={{
        headerTitle: () => <HeaderTitle />,
        headerRight: () => <NotificationBell onPress={() => setDrawerOpen(true)} />,
        tabBarStyle: {
          backgroundColor: colors.white,
          borderTopColor: colors.line,
          borderTopWidth: 1,
          height: tabHeight,
          paddingTop: 6,
          paddingBottom: bottomPadding,
        },
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: "700",
          letterSpacing: 0.2,
        },
        tabBarActiveTintColor: colors.brand,
        tabBarInactiveTintColor: colors.inkMuted,
        headerTitleAlign: "center",
        headerLeftContainerStyle: { width: 60 },
        headerRightContainerStyle: { width: 60 },
        headerStyle: {
          backgroundColor: colors.white,
          shadowColor: "transparent",
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
        },
        sceneStyle: {
          backgroundColor: colors.bg,
        },
      }}
    >
      <Tabs.Screen
        name="index"
        options={{
          title: "Feed",
          tabBarIcon: ({ color }) => <HomeIcon color={color} />,
        }}
      />
      <Tabs.Screen
        name="matches"
        options={{
          title: "Fixtures",
          tabBarIcon: ({ color }) => <CalendarIcon color={color} />,
        }}
      />
      <Tabs.Screen
        name="channels"
        options={{
          title: "Chat",
          tabBarIcon: ({ color }) => <ChatIcon color={color} />,
          tabBarBadge: unreadChannelCount > 0 ? unreadChannelCount : undefined,
          tabBarBadgeStyle: { backgroundColor: colors.danger, color: "#fff", fontSize: 10 },
        }}
      />
      <Tabs.Screen
        name="events"
        options={{
          title: "Events",
          tabBarIcon: ({ color }) => <TicketIcon color={color} />,
        }}
      />
      <Tabs.Screen
        name="club"
        options={{
          title: "Club",
          tabBarIcon: ({ color }) => <ShieldIcon color={color} />,
          tabBarBadge: needsPaymentSetup ? "" : undefined,
          tabBarBadgeStyle: needsPaymentSetup ? {
            backgroundColor: colors.danger,
            minWidth: 10,
            maxHeight: 10,
            borderRadius: 5,
            top: 2,
          } : undefined,
        }}
      />
    </Tabs>
    <NotificationsDrawer visible={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </View>
  );
}
